import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Building2, Plus, Search, Sparkles, ArrowLeft, Layers, ClipboardList,
  Clock, AlertTriangle, Lightbulb, GitMerge, BarChart3, Trash2, Copy } from "lucide-react";
import { OrgPicker } from "./OrgPicker";

const FIELD_GROUPS = [
  { label: "Identity & taxonomy", fields: [
    { k: "legal_name", t: "text", label: "Legal name" },
    { k: "sector", t: "sector", label: "Sector" },
    { k: "geography", t: "enum", label: "Geography", opts: ["local", "national", "regional", "global"] },
    { k: "size_band", t: "enum", label: "Size band", opts: ["sme", "mid_market", "enterprise", "public_body"] },
  ]},
  { label: "Operating profile (ENTERPRISE360 superset)", fields: [
    { k: "regulation", t: "enum", label: "Regulation", opts: ["low", "medium", "high", "critical"] },
    { k: "supply_chain_model", t: "enum", label: "Supply chain", opts: ["local", "national", "regional", "global", "vertically_integrated"] },
    { k: "customer_model", t: "enum", label: "Customer model", opts: ["b2b", "b2c", "b2b2c", "subscription", "public"] },
    { k: "product_model", t: "enum", label: "Product model", opts: ["physical_product", "digital_product", "service", "subscription", "project"] },
    { k: "operating_model", t: "enum", label: "Operating model", opts: ["centralised", "federated", "decentralised", "matrixed"] },
    { k: "ambition", t: "enum", label: "Ambition", opts: ["stabilise", "optimise", "scale", "transform"] },
    { k: "delivery_maturity", t: "enum", label: "Delivery maturity", opts: ["foundational", "scaling", "established", "advanced"] },
    { k: "annual_value_base_gbp", t: "number", label: "Annual value base (£)" },
  ]},
];

export const OrganisationsPanel = () => {
  const [orgs, setOrgs] = useState([]);
  const [meta, setMeta] = useState({ sectors: [] });
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // org detail
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [view, setView] = useState("list"); // list | portfolio
  const [portfolio, setPortfolio] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/organisations", { params: q ? { q } : {} })
      .then(({ data }) => setOrgs(data.organisations || [])).catch(() => {});
  }, [q]);

  useEffect(() => { api.get("/admin/organisations/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (view === "portfolio" && !portfolio) {
      api.get("/admin/organisations/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
    }
  }, [view, portfolio]);

  // Duplicate guard on the create form name.
  useEffect(() => {
    if (!creating || !draft.legal_name?.trim()) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/organisations/match", { params: { name: draft.legal_name.trim() } })
        .then(({ data }) => setDupes(data.matches || [])).catch(() => setDupes([]));
    }, 350);
    return () => clearTimeout(t);
  }, [draft.legal_name, creating]);

  const openOrg = (id) => api.get(`/admin/organisations/${id}`).then(({ data }) => setActive(data)).catch(() => toast.error("Could not open organisation"));

  const [mergeOpen, setMergeOpen] = useState(false);
  const mergeInto = async (survivor) => {
    if (!active || !survivor) return;
    if (survivor.id === active.id) { toast.error("Pick a different organisation to merge into"); return; }
    if (!window.confirm(`Merge "${active.legal_name}" into "${survivor.legal_name}"? Linked runs & engagements move to the survivor and this record is deleted.`)) return;
    try {
      const { data } = await api.post(`/admin/organisations/${active.id}/merge`, { into: survivor.id });
      toast.success(`Merged — ${data.repointed_runs} run(s) + ${data.repointed_engagements} engagement(s) moved`);
      setMergeOpen(false);
      setActive(null); setPortfolio(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  const deleteOrg = async () => {
    if (!active) return;
    if (!window.confirm(`Delete "${active.legal_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/organisations/${active.id}`);
      toast.success("Organisation deleted");
      setActive(null); setPortfolio(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const [dupClusters, setDupClusters] = useState(null);
  useEffect(() => {
    if (view === "duplicates" && !dupClusters) {
      api.get("/admin/organisations/duplicates").then(({ data }) => setDupClusters(data)).catch(() => setDupClusters({ clusters: [] }));
    }
  }, [view, dupClusters]);
  const mergeInCluster = async (sourceId, targetId, sourceName, targetName) => {
    if (!window.confirm(`Merge "${sourceName}" into "${targetName}"?`)) return;
    try {
      const { data } = await api.post(`/admin/organisations/${sourceId}/merge`, { into: targetId });
      toast.success(`Merged — ${data.repointed_runs} run(s) + ${data.repointed_engagements} engagement(s) moved`);
      setDupClusters(null); setPortfolio(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  const runBackfill = async (apply) => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/organisations/backfill", null, { params: { apply } });
      setBackfill(data);
      if (apply) {
        toast.success(`Backfill applied — ${data.created} created, ${data.linked_runs} runs + ${data.linked_engagements} engagements linked`);
        setPortfolio(null); load();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    finally { setBackfilling(false); }
  };

  const create = async () => {
    if (!draft.legal_name?.trim()) { toast.error("Legal name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/organisations", draft);
      toast.success("Organisation created");
      setCreating(false); setDraft({}); load(); openOrg(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  const saveField = async (k, v) => {
    try {
      const { data } = await api.patch(`/admin/organisations/${active.id}`,
        { [k]: v === "" ? null : v });
      setActive((a) => ({ ...a, ...data }));
      load();
    } catch (e) { toast.error("Could not save"); }
  };

  const Field = ({ f, value, onChange }) => {
    if (f.t === "text" || f.t === "number") {
      return <input type={f.t === "number" ? "number" : "text"} value={value ?? ""}
        onChange={(e) => onChange(f.t === "number" ? e.target.value : e.target.value)}
        data-testid={`org-field-${f.k}`}
        className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px] ek-surface" />;
    }
    if (f.t === "sector") {
      return <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`org-field-${f.k}`} className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px] ek-surface">
        <option value="">—</option>
        {(meta.sectors || []).map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
      </select>;
    }
    return <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={`org-field-${f.k}`} className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px] ek-surface">
      <option value="">—</option>
      {f.opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>;
  };

  // ---- Detail view ----
  if (active) {
    const pct = active.completeness_pct || 0;
    return (
      <div className="space-y-5" data-testid="org-detail">
        <button onClick={() => { setActive(null); load(); }} data-testid="org-back"
          className="inline-flex items-center gap-1.5 text-[12px] ek-text-muted hover:ek-text"><ArrowLeft size={14} /> All organisations</button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="ek-heading text-xl flex items-center gap-2"><Building2 size={19} /> {active.legal_name}</h2>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {active.archetype_matched
                ? <span className="inline-flex items-center gap-1.5 text-[12px] text-[#7737F6] bg-violet-50 border border-violet-200 rounded-full px-3 py-1" data-testid="org-archetype"><Sparkles size={12} /> {active.archetype_name}</span>
                : <span className="text-[12px] ek-text-muted" data-testid="org-archetype">Archetype: not yet resolvable — complete more of the profile</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] ek-text-muted uppercase tracking-[0.12em]">Profile completeness</div>
            <div className="text-2xl font-semibold text-[#7737F6] tabular-nums" data-testid="org-completeness">{pct}%</div>
            <div className="w-40 h-2 rounded-full ek-surface-2 overflow-hidden mt-1">
              <div className="h-2 rounded-full bg-[#7737F6]" style={{ width: `${pct}%` }} />
            </div>
            <button onClick={() => setMergeOpen((v) => !v)} data-testid="org-merge-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] ek-text-muted hover:text-[#7737F6]"><GitMerge size={13} /> Merge into…</button>
            <button onClick={deleteOrg} data-testid="org-delete-btn"
              className="mt-3 ml-3 inline-flex items-center gap-1.5 text-[11px] ek-text-muted hover:text-rose-600"><Trash2 size={13} /> Delete</button>
          </div>
        </div>

        {mergeOpen && (
          <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3.5" data-testid="org-merge-panel">
            <div className="text-[12px] font-medium ek-text mb-1 flex items-center gap-1.5"><GitMerge size={13} /> Merge this organisation into another</div>
            <p className="text-[11.5px] ek-text-muted mb-2">Choose the surviving organisation. All assessments & engagements move to it; blank survivor fields are filled from this record; this record is then deleted.</p>
            <OrgPicker selected={null} onSelect={mergeInto} label="Survivor organisation" />
          </div>
        )}

        {(active.next_suggestions || []).length > 0 && (
          <div className="border border-violet-200 bg-violet-50/60 rounded-lg p-3.5 flex items-start gap-2.5" data-testid="org-completeness-nudge">
            <Lightbulb size={16} className="text-[#7737F6] shrink-0 mt-0.5" />
            <div>
              <div className="text-[12.5px] font-medium ek-text">
                {active.archetype_matched
                  ? "Add a few more fields to sharpen this organisation's archetype"
                  : "Capture these next to unlock an archetype"}
              </div>
              <div className="text-[12px] ek-text-muted mt-0.5">
                Suggested next: {active.next_suggestions.map((s) => s.label).join(" · ")}
              </div>
            </div>
          </div>
        )}

        {FIELD_GROUPS.map((g) => (
          <div key={g.label}>
            <h3 className="ek-text-soft text-[11px] uppercase tracking-[0.14em] mb-2">{g.label}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.fields.map((f) => (
                <div key={f.k}>
                  <label className="text-[11px] ek-text-muted">{f.label}</label>
                  <Field f={f} value={active[f.k]} onChange={(v) => saveField(f.k, v)} />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div>
          <h3 className="ek-text-soft text-[11px] uppercase tracking-[0.14em] mb-2 flex items-center gap-1.5">
            <Clock size={13} /> Relationship timeline ({(active.timeline || []).length})
          </h3>
          <div className="border ek-line rounded-lg overflow-hidden" data-testid="org-timeline">
            {(active.timeline || []).length === 0 ? (
              <p className="text-[12px] ek-text-muted p-4">No assessments or engagements linked yet — link this organisation from a new assessment run or engagement.</p>
            ) : active.timeline.map((t) => (
              <div key={`${t.type}-${t.id}`} data-testid={`org-timeline-${t.id}`}
                className="flex items-center gap-3 px-3.5 py-2.5 border-t first:border-t-0 ek-line">
                <span className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.1em] rounded-full px-2 py-0.5 shrink-0 ${t.type === "engagement" ? "bg-[#7737F6]/10 text-[#7737F6]" : "ek-surface-2 ek-text-soft"}`}>
                  {t.type === "engagement" ? <Layers size={11} /> : <ClipboardList size={11} />}
                  {t.type === "engagement" ? "Engagement" : "Assessment"}
                </span>
                <span className="text-[12.5px] ek-text truncate flex-1">{t.name}</span>
                {t.status && <span className="text-[11px] ek-text-muted shrink-0">{t.status}</span>}
                <span className="text-[11px] ek-text-muted tabular-nums shrink-0">{(t.at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-4" data-testid="organisations-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Building2 size={19} /> Organisations</h2>
          <p className="text-[12px] ek-text-muted mt-0.5 max-w-2xl">One canonical company record shared by Assessment (EKSELENS360) and engagements (ENTERPRISE360). Capture the company once, complete the profile progressively, and reuse its archetype everywhere.</p>
        </div>
        <button onClick={() => { setCreating(true); setDraft({}); setView("list"); }} data-testid="org-new-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] bg-[#7737F6] text-white rounded-lg hover:bg-[#6528e0]"><Plus size={15} /> New organisation</button>
      </div>

      <div className="flex items-center gap-1 border-b ek-line" data-testid="org-view-tabs">
        <button onClick={() => setView("list")} data-testid="org-tab-list"
          className={`px-3 py-2 text-[12px] font-medium tracking-[0.06em] uppercase border-b-2 -mb-px ${view === "list" ? "border-[#7737F6] text-[#7737F6]" : "border-transparent ek-text-muted hover:ek-text"}`}>Organisations</button>
        <button onClick={() => setView("portfolio")} data-testid="org-tab-portfolio"
          className={`px-3 py-2 text-[12px] font-medium tracking-[0.06em] uppercase border-b-2 -mb-px inline-flex items-center gap-1.5 ${view === "portfolio" ? "border-[#7737F6] text-[#7737F6]" : "border-transparent ek-text-muted hover:ek-text"}`}><BarChart3 size={13} /> Portfolio &amp; backfill</button>
        <button onClick={() => setView("duplicates")} data-testid="org-tab-duplicates"
          className={`px-3 py-2 text-[12px] font-medium tracking-[0.06em] uppercase border-b-2 -mb-px inline-flex items-center gap-1.5 ${view === "duplicates" ? "border-[#7737F6] text-[#7737F6]" : "border-transparent ek-text-muted hover:ek-text"}`}><Copy size={13} /> Duplicates</button>
      </div>

      {view === "duplicates" ? (
        <div className="space-y-4" data-testid="org-duplicates">
          <p className="text-[12.5px] ek-text-muted">Organisations that look like the same company (identical name/alias or strong name overlap). Merge each cluster into a single record.</p>
          {!dupClusters ? (
            <p className="text-[12px] ek-text-muted">Scanning…</p>
          ) : dupClusters.clusters.length === 0 ? (
            <div className="border ek-line rounded-lg p-6 text-center text-[13px] ek-text-muted" data-testid="org-duplicates-empty">No likely duplicates found — your organisation book looks clean. 🎉</div>
          ) : dupClusters.clusters.map((cl, i) => {
            const survivor = cl.organisations[0];
            return (
              <div key={i} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3.5" data-testid={`org-dupe-cluster-${i}`}>
                <div className="text-[12px] font-medium ek-text mb-2">{cl.count} likely duplicates · suggested survivor: <span className="text-[#7737F6]">{survivor.legal_name}</span> ({survivor.completeness_pct || 0}%)</div>
                <div className="space-y-1.5">
                  {cl.organisations.map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-2 text-[12.5px]">
                      <button onClick={() => { setView("list"); openOrg(o.id); }} className="ek-text hover:text-[#7737F6] truncate text-left">
                        {o.legal_name} <span className="ek-text-muted">· {o.sector || "—"} · {o.completeness_pct || 0}%</span>
                      </button>
                      {o.id === survivor.id ? (
                        <span className="text-[10.5px] uppercase tracking-[0.1em] bg-[#7737F6]/10 text-[#7737F6] rounded-full px-2 py-0.5 shrink-0">Survivor</span>
                      ) : (
                        <button onClick={() => mergeInCluster(o.id, survivor.id, o.legal_name, survivor.legal_name)}
                          data-testid={`org-dupe-merge-${o.id}`}
                          className="text-[11px] text-amber-800 hover:underline inline-flex items-center gap-1 shrink-0"><GitMerge size={12} /> Merge into survivor</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : view === "portfolio" ? (
        <div className="space-y-4" data-testid="org-portfolio">
          {portfolio && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                ["Organisations", portfolio.totals.organisations],
                ["With activity", portfolio.totals.with_activity],
                ["Assessments linked", portfolio.totals.assessments_linked],
                ["Engagements linked", portfolio.totals.engagements_linked],
                ["Avg completeness", `${portfolio.totals.avg_completeness}%`],
              ].map(([label, val]) => (
                <div key={label} className="border ek-line rounded-lg p-3 ek-surface">
                  <div className="text-[10.5px] uppercase tracking-[0.12em] ek-text-muted">{label}</div>
                  <div className="text-2xl font-semibold text-[#7737F6] tabular-nums">{val}</div>
                </div>
              ))}
            </div>
          )}

          <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4" data-testid="org-backfill-card">
            <div className="flex items-start gap-2.5">
              <GitMerge size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-[13px] font-semibold ek-text">Backfill existing records</h3>
                <p className="text-[12px] ek-text-muted mt-0.5 max-w-2xl">Group existing assessment runs and engagements that aren't linked yet by company name, then link them to a matching or newly-created organisation. Preview first — nothing is written until you apply. Non-destructive: existing links and snapshots are never overwritten.</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => runBackfill(false)} disabled={backfilling} data-testid="org-backfill-preview"
                    className="px-3 py-1.5 text-[12px] border ek-line rounded ek-text-soft disabled:opacity-50">{backfilling ? "Working…" : "Preview (dry run)"}</button>
                  {backfill && backfill.groups > 0 && (
                    <button onClick={() => runBackfill(true)} disabled={backfilling} data-testid="org-backfill-apply"
                      className="px-3 py-1.5 text-[12px] bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">Apply — link {backfill.groups} group(s)</button>
                  )}
                </div>
                {backfill && (
                  <div className="mt-3 text-[12px]" data-testid="org-backfill-result">
                    <div className="ek-text-soft mb-1">{backfill.dry_run ? "Dry run — " : "Applied — "}{backfill.groups} group(s); {backfill.created} to create/created, {backfill.linked_runs} run(s) + {backfill.linked_engagements} engagement(s).</div>
                    {(backfill.plan || []).slice(0, 30).map((p, i) => (
                      <div key={i} className="flex items-center gap-2 py-0.5 ek-text-muted">
                        <span className={`text-[10px] uppercase rounded px-1.5 py-0.5 ${p.action === "create" ? "bg-violet-100 text-[#7737F6]" : "bg-zinc-100 text-zinc-600"}`}>{p.action === "create" ? "new" : "link"}</span>
                        <span className="ek-text truncate">{p.name}</span>
                        <span className="shrink-0">{p.runs} run(s) · {p.engagements} eng.</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border ek-line rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead><tr className="ek-surface-2 text-left ek-text-soft">
                <th className="p-2.5 font-medium">Organisation</th><th className="p-2.5 font-medium">Sector</th>
                <th className="p-2.5 font-medium">Archetype</th><th className="p-2.5 font-medium text-center">Assessments</th>
                <th className="p-2.5 font-medium text-center">Engagements</th><th className="p-2.5 font-medium">Complete</th></tr></thead>
              <tbody>
                {(portfolio?.rows || []).length === 0 ? (
                  <tr><td colSpan={6} className="p-4 text-center ek-text-muted text-[12.5px]">No organisations yet.</td></tr>
                ) : portfolio.rows.map((o) => (
                  <tr key={o.id} onClick={() => { setView("list"); openOrg(o.id); }} data-testid={`org-portfolio-row-${o.id}`}
                    className="border-t ek-line cursor-pointer hover:bg-zinc-50">
                    <td className="p-2.5 ek-text font-medium">{o.legal_name}</td>
                    <td className="p-2.5 ek-text-soft">{o.sector_label || o.sector || "—"}</td>
                    <td className="p-2.5 ek-text-muted">{o.archetype_name || "—"}</td>
                    <td className="p-2.5 text-center tabular-nums ek-text-soft">{o.assessments}</td>
                    <td className="p-2.5 text-center tabular-nums ek-text-soft">{o.engagements}</td>
                    <td className="p-2.5 tabular-nums ek-text-soft">{o.completeness_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <>
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-2.5 top-2.5 ek-text-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
          data-testid="org-search" className="w-full border ek-line rounded-lg pl-8 pr-3 py-1.5 text-[13px] ek-surface" />
      </div>

      {creating && (
        <div className="border border-violet-200 bg-violet-50/40 rounded-lg p-4 space-y-3" data-testid="org-create-form">
          <h3 className="text-[13px] font-semibold ek-text">New organisation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] ek-text-muted">Legal name *</label>
              <input value={draft.legal_name || ""} onChange={(e) => setDraft((d) => ({ ...d, legal_name: e.target.value }))}
                data-testid="org-create-name" className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px] ek-surface" />
            </div>
            <div>
              <label className="text-[11px] ek-text-muted">Sector</label>
              <select value={draft.sector || ""} onChange={(e) => setDraft((d) => ({ ...d, sector: e.target.value }))}
                data-testid="org-create-sector" className="w-full border ek-line rounded px-2.5 py-1.5 text-[13px] ek-surface">
                <option value="">—</option>
                {(meta.sectors || []).map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </select>
            </div>
          </div>
          {dupes.length > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-2.5" data-testid="org-create-dupe-warning">
              <div className="text-[11px] font-medium text-amber-800 mb-1 flex items-center gap-1.5">
                <AlertTriangle size={12} /> Possible duplicate — open an existing organisation instead?
              </div>
              <div className="space-y-1">
                {dupes.map((d) => (
                  <button key={d.id} onClick={() => { setCreating(false); openOrg(d.id); }} data-testid={`org-create-dupe-${d.id}`}
                    className="w-full text-left text-[12px] text-amber-900 hover:underline flex items-center justify-between gap-2">
                    <span className="truncate">{d.legal_name}</span>
                    <span className="text-[11px] text-amber-700 shrink-0">{d.archetype_name || d.sector || "—"} · Open</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={create} disabled={saving} data-testid="org-create-submit"
              className="px-3 py-1.5 text-[13px] bg-[#7737F6] text-white rounded hover:bg-[#6528e0] disabled:opacity-50">{saving ? "Creating…" : "Create"}</button>
            <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-[13px] border ek-line rounded ek-text-soft">Cancel</button>
          </div>
        </div>
      )}

      <div className="border ek-line rounded-lg overflow-hidden">
        <table className="w-full text-[13px]">
          <thead><tr className="ek-surface-2 text-left ek-text-soft">
            <th className="p-2.5 font-medium">Organisation</th><th className="p-2.5 font-medium">Sector</th>
            <th className="p-2.5 font-medium">Archetype</th><th className="p-2.5 font-medium">Complete</th></tr></thead>
          <tbody>
            {orgs.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center ek-text-muted text-[12.5px]" data-testid="org-empty">No organisations yet — create your first, or they'll appear as you link assessments and engagements.</td></tr>
            ) : orgs.map((o) => (
              <tr key={o.id} onClick={() => openOrg(o.id)} data-testid={`org-row-${o.id}`}
                className="border-t ek-line cursor-pointer hover:bg-zinc-50">
                <td className="p-2.5 ek-text font-medium">{o.legal_name}</td>
                <td className="p-2.5 ek-text-soft">{(meta.sectors || []).find((s) => s.slug === o.sector)?.label || o.sector || "—"}</td>
                <td className="p-2.5 ek-text-muted">{o.archetype_name || "—"}</td>
                <td className="p-2.5 tabular-nums ek-text-soft">
                  {o.completeness_pct || 0}%
                  {o.missing_count > 0 && (
                    <span className="ml-2 text-[10.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5" data-testid={`org-nudge-${o.id}`}>
                      {o.missing_count} to add
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
};

export default OrganisationsPanel;
